import { Component, Element, h, Host, Method, Prop, State, Event, EventEmitter } from "@stencil/core";
import smoothscroll from "smoothscroll-polyfill";
import { Uppy } from "@uppy/core";
import ProgressBar from "@uppy/progress-bar";
import ThumbnailGenerator from "@uppy/thumbnail-generator";
import AwsS3 from "@uppy/aws-s3";
import { hexToRGBA, AppStoreType, AppStateType } from "../../utils/utils";
import { SVGDropdown, SVGBrand, SVGCompleted, SVGStar } from "./covet-pics-upload-svg";
import { setUploadOpen } from "../../store/modals";
import { Unsubscribe } from "../../utils/types";
import { IconDown } from "../../utils/funcComponents";

const UPLOAD_FAILED_TEXT = "Something went wrong. Try again later.";

@Component({
  tag: "covet-pics-upload",
  styleUrl: "covet-gallery-upload.scss",
  shadow: true
})

export class CovetPicsUpload {
  @Element() el: HTMLCovetPicsUploadElement;

  private uppy;

  /**
  * Photo request ID from email
  */
  @Prop() photoRequestId: number;

  /**
  * Shows whether upload widget loads opened
  * and behave a standalone widget
  */
  @Prop() standalone: boolean = false;

  /**
   * App store
   */
  @Prop() appStore: AppStoreType;

  // App state;
  private appState: AppStateType;
  private unsubscribe: Unsubscribe;
  private isTextReview: boolean = false;

  @State() starRating: number = 0;

  @State() isFormValid: boolean = false;

  @State() showFormWarning: boolean = false; 

  @State() thumbnailLinks: string[] = [];

  @State() toShowLinks: string[] = [];

  @State() dropedImagesNumber: number = 0;

  @State() isAnimating: boolean = false;

  @State() notificationMessage: string = "";

  @State() isNotificationActive: boolean = false;

  @State() widgetWidth: number;

  @State() uploadTabIndex: number = -1;

  /**
   * emitted when Upload is open (JS API)
  */
  @Event({
    eventName: "uploadOpen:covetPics",
    composed: false,
    cancelable: true,
    bubbles: true,
  }) uploadOpen: EventEmitter;

  /**
   * emitted when Upload is closed (JS API)
  */
  @Event({
    eventName: "uploadClose:covetPics",
    composed: false,
    cancelable: true,
    bubbles: true,
  }) uploadClose: EventEmitter;

  /**
   * Open upload popup
   */
  @Method()
  async openUpload() {
    const popup = this.el.shadowRoot.querySelector(".popup") as HTMLElement;

    this.el.classList.add("covet-pics-popup-show", "covet-pics-popup-open");
    popup.classList.add("open");

    // prevent body to scroll setting overflow: hidden
    document.body.classList.add("covet-pics-popup-scroll-lock");
    this.uploadOpen.emit();
    this.appStore.dispatch(setUploadOpen(true));
    this.uploadTabIndex = 1;
    popup.focus();
  }

  componentWillLoad() {
    this.initStore();
  }
  
  componentDidLoad() {
    smoothscroll.polyfill();
    this.initUppy();
    if (this.standalone) { this.openUpload(); }
  }

  disconnectedCallback() {
    this.unsubscribe();
  }
  
  private initStore() {
    this.appState = this.appStore.getState();
    this.widgetWidth = this.appState.size.widgetWidth;
    this.unsubscribe = this.appStore.subscribe(() => {
      this.appState = this.appStore.getState();
      this.widgetWidth = this.appState.size.widgetWidth;
    });
  }

  private select(selector: string) {
    return this.el.shadowRoot.querySelector(selector);
  }

  private selectAll(selector: string) {
    return this.el.shadowRoot.querySelectorAll(selector);
  }

  private initDefaultSettings() {
    this.thumbnailLinks = [];
    this.toShowLinks = [];
    this.dropedImagesNumber = 0;
    this.starRating = 0;

    const images = Array.from(this.selectAll(".upload-img-wrap"));
    images.forEach(image => image.classList.remove("enter", "leave", "leave-to"));
    images.forEach(image => image.classList.add("before-enter"));
    const imgCount = this.select(".upload-img-count");
    imgCount.classList.remove("enter");
    const step1 = this.select(".upload-step.step1");
    step1.classList.remove("no-skip-btn");
    return true;
  }

  private resetSkipSettings() {
    this.isTextReview = false;
    const skipBtn = this.select(".btn-skip");
    skipBtn.classList.remove("hidden");
    const step1 = this.select(".upload-step.step1");
    const step2 = this.select(".upload-step.step2");
    const step3 = this.select(".upload-step.step3");
    const step4 = this.select(".upload-step.step4");
    return setTimeout(() => {
      step1.classList.remove("enter", "no-skip-btn", "leave", "leave-to", "hidden");
      step2.classList.remove("enter", "no-margin", "leave");
      step2.classList.add("hidden");
      step3.classList.remove("enter", "leave", "leave-to");
      step3.classList.add("hidden");
      step4.classList.remove("enter", "leave", "leave-to");
      step4.classList.add("hidden");
    }, 300);
  }

  private postData: object = {
    caption: "",
    source_user_id: "",
    source_user_email: "",
    rating: null
  }

  private handleSendClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    return this.isFormValid 
      ? this.showStep3()
      : (this.showFormWarning = true);
  }

  private handleCloseClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    this.closeUploadWidget();
    this.showFromStep4ToStep1();
  }

  private postDataChangeHandler = (event) => {
    this.showFormWarning = false;
    this.postData[event.target.name] = event.target.value;
    this.onFormSendEnable();
  }

  private onFormSendEnable() {
    this.isFormValid = (this.isInputValid("source_user_id") && this.isInputValid("source_user_email") && this.isInputValid("caption") && this.postData["rating"] !== null);
    return this.isFormValid;
  }

  private isInputValid(name: string): boolean {
    const el = this.el.shadowRoot.querySelector(`[name='${name}']`) as HTMLInputElement;
    return el !== null ? el.checkValidity() : false;
  }

  private onDropHandler = (event) => {
    let dt = event.dataTransfer;
    let files = [...dt.files];
    this.dropedImagesNumber = files.length;

    event.preventDefault();
    event.stopPropagation();

    files.forEach(file => {
      this.uppy.addFile({
        name: file.name,
        type: file.type,
        data: file,
        source: "DragDrop",
        isRemote: false,
      });
    });
  }

  private onDragOverHandler = (event: DragEvent) => {
    event.stopPropagation();
    event.preventDefault();
  }

  private openDialog = (_event) => {
    const input: HTMLElement = this.el.shadowRoot.querySelector("input[type=file]") as HTMLElement;
    input.click();
  }

  private onChangeHandler = (event) => {
    let files = [...event.target.files];
    this.dropedImagesNumber = event.target.files.length;

    event.preventDefault();
    event.stopPropagation();

    // TODO add notification, if the file has already been added
    files.forEach(file => {
      this.uppy.addFile({
        name: file.name,
        type: file.type,
        data: file,
        source: "DragDrop",
        isRemote: false,
      });
    });
  }

  private hidePopupClickHandler = (event: UIEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.standalone) { return false; }

    this.closeUploadWidget();
  }

  private closeUploadWidget() {
    this.el.classList.remove("covet-pics-popup-open");
    if (this.isTextReview) this.resetSkipSettings();
    // TODO add close animation, and clear all
    setTimeout(() => {
      const openPopupEl = this.el.shadowRoot.querySelector(".popup.open") as HTMLElement;
      openPopupEl.classList.remove("open");
      this.el.classList.remove("covet-pics-popup-show");
    }, this.appState.settings.popup_animation ? 300 : 0);

    // get back to normal scroll state removing body overflow
    document.body.classList.remove("covet-pics-popup-scroll-lock");
    this.uploadClose.emit();
    this.appStore.dispatch(setUploadOpen(false));
    this.uploadTabIndex = -1;
  }

  private initUppy() {
    this.uppy = new Uppy({
      debug: process.env.NODE_ENV !== "production",
      restrictions: {
        maxFileSize: 20000000,
        maxNumberOfFiles: 4,
        minNumberOfFiles: 1,
        allowedFileTypes: ["image/png", "image/gif", "image/jpg", "image/jpeg"]
      }
    });

    this.uppy
      .use(ProgressBar, {
        target: this.el.shadowRoot.querySelector(".progress-bar"),
        hideAfterFinish: false,
      })
      .use(ThumbnailGenerator, {
        waitForThumbnailsBeforeUpload: false
      })
      .use(AwsS3, {
        companionUrl: `${this.appState.url.baseUrl}/presign/media`,
      });

    // when file selected and get thumbnail, move to step 2
    this.uppy.on("thumbnail:generated", (file) => {
      this.toShowLinks = [...this.toShowLinks, file.preview];
      if (this.toShowLinks.length === this.dropedImagesNumber) this.imagesDrop();
    });

    this.uppy.on("restriction-failed", (_file, error) => {
      const clearAllErrorFile = (files: any[]) => {
        files.forEach(file => {
          if (file.preview === undefined) {
            this.uppy.removeFile(file.id);
          }
        });
      };
      const dropedfiles = this.uppy.getFiles();
      clearAllErrorFile(dropedfiles);
      this.showNotification(error.message);
    });

    this.uppy.on("complete", (result) => {
      const request = new XMLHttpRequest();
      const payload = {
        gallery_id: this.appState.widgetId.galleryId,
        gallery_embed_id: this.appState.widgetId.embedId,
        photo_request_id: this.photoRequestId,
        product_handle: this.appState.widgetId.productHandle,
        post: {
          source_user_email: this.postData["source_user_email"],
          caption: this.postData["caption"],
          source_user_id: this.postData["source_user_id"],
          rating: this.postData["rating"],
          custom_photo_data: [],
        }
      };
      const completeCallback = (success) => {
        const successIcon = this.select(".upload-step.step4 svg");
        const completeText = this.select(".upload-step.step4 .upload-text.completed");

        successIcon.classList.toggle("invisible", !success);
        if (success) {
          completeText.innerHTML = this.appState.settings.customer_photo_upload_complete_body;
        } else {
          completeText.innerHTML = UPLOAD_FAILED_TEXT;
        }

        this.showStep4();
      };

      result.successful.forEach(uploadedImage => {
        // temp. fix - Cloduflare doesn't support POST for R2 (only PUT)
        // https://github.com/transloadit/uppy/issues/724
        // POST request has .meta["key"]
        // PUT request has .uploadURL
        // Cloudflare supports only PUT

        let fileId = "";
        if (uploadedImage.meta["key"]) {
          fileId = uploadedImage.meta["key"].match(/^cache\/(.+)/)[1]; // object key without prefix
        }
        else {
          fileId = uploadedImage.uploadURL.split("/").pop().split("#")[0].split("?")[0];
        }

        payload.post.custom_photo_data.push({
          id: fileId,
          storage: "cache",
          metadata: {
            size: uploadedImage.size,
            filename: uploadedImage.name,
            mime_type: uploadedImage.type,
          }
        });
      });

      // skip sending API request if there are no images to upload, and show error message
      if (payload.post.custom_photo_data.length === 0) {
        completeCallback(false);
        return;
      }

      request.onload = () => {
        const response = JSON.parse(request.responseText);
        completeCallback(response.success);
      };

      request.open("POST", `${this.appState.url.baseUrl}/api/v1/posts`);
      request.setRequestHeader("Accept", "application/json");
      request.setRequestHeader("Content-Type", "application/json; charset=UTF-8");
      request.send(JSON.stringify(payload));
    });
  }

  private uploadTextReview() {
    const request = new XMLHttpRequest();
    const payload = {
      gallery_id: this.appState.widgetId.galleryId,
      gallery_embed_id: this.appState.widgetId.embedId,
      photo_request_id: this.photoRequestId,
      product_handle: this.appState.widgetId.productHandle,
      post: {
        source_user_email: this.postData["source_user_email"],
        caption: this.postData["caption"],
        source_user_id: this.postData["source_user_id"],
        rating: this.postData["rating"],
        custom_photo_data: [],
      }
    };

    const completeCallback = (success) => {
      const successIcon = this.select(".upload-step.step4 svg");
      const completeText = this.select(".upload-step.step4 .upload-text.completed");

      successIcon.classList.toggle("invisible", !success);
      if (success) {
        completeText.innerHTML = this.appState.settings.customer_photo_upload_complete_body;
      } else {
        completeText.innerHTML = UPLOAD_FAILED_TEXT;
      }

      this.showStep4();
    };

    request.onload = () => {
      const response = JSON.parse(request.responseText);
      completeCallback(response.success);
    };

    request.open("POST", `${this.appState.url.baseUrl}/api/v1/posts`);
    request.setRequestHeader("Accept", "application/json");
    request.setRequestHeader("Content-Type", "application/json; charset=UTF-8");
    request.send(JSON.stringify(payload));
  }

  private showNotification(message: string) {
    this.isNotificationActive = true;
    this.notificationMessage = message;
    return setTimeout(() => {
      this.isNotificationActive = false;
      this.notificationMessage = message;
    }, 3000);
  }

  private setPopupCSSVariables() {
    let cssVariables = {
      "--covet-pics-popup-background-color": hexToRGBA(this.appState.settings.popup_background_color, (this.appState.settings.popup_background_opacity / 100).toString()),
      "--covet-pics-scrollbar-width": `${this.getScrollbarWidth().width}px`,
      "--covet-pics-scrollbar-padding": `${this.getScrollbarWidth().paddingRgiht}px`,
      "--covet-pics-upload-img-ratio": `${this.widgetWidth < 480 ? (this.widgetWidth - 48) / 352 : 1}`, // TODO temp solution
    };
    if (this.appState.settings.font_family !== "") {
      cssVariables["--covet-pics-font-family"] = `${this.appState.settings.font_family}, sans-serif`;
    }

    return cssVariables;
  }

  private starClickHandler = (event: MouseEvent, index: number) => {
    event.preventDefault();
    event.stopPropagation();
    this.postData["rating"] = index + 1;
    this.onFormSendEnable();
    return (this.starRating = index + 1);
  }

  private async imagesDrop() {
    this.hideSkipBtn();
    if (this.thumbnailLinks.length === 0) {
      this.thumbnailLinks = [...this.toShowLinks];
      await this.animationDelay(20);
      this.toShowLinks = [];
      return this.showStep2();
    } else {
      await this.hideImages();
      this.thumbnailLinks = [...this.thumbnailLinks.concat(this.toShowLinks)];
      await this.animationDelay(20);
      this.toShowLinks = [];
      return this.showImages();
    }
  }

  private async hideImages() {
    const images = Array.from(this.selectAll(".upload-img-wrap"));
    await this.animateGroupEl(images, "leave");
    images.forEach(image => image.classList.remove("enter"));
    await this.animationDelay(10);
    images.forEach(image => image.classList.add("before-enter"));
    return images.forEach(image => image.classList.remove("leave"));
  }

  private async showImages() {
    const images = Array.from(this.selectAll(".upload-img-wrap"));
    await this.animateGroupEl(images, "enter");
    return images.forEach(image => image.classList.remove("before-enter"));
  }

  private async hideSkipBtn() {
    const step1 = this.select(".upload-step.step1"); 
    const skipBtn = this.select(".btn-skip"); 
    await this.animateEl(step1, "no-skip-btn");
    skipBtn.classList.add("hidden");
  }

  private async showSkipToStep2() {
    if (this.isAnimating) return true;
    this.isAnimating = true;
    this.isTextReview = true;

    const upload = this.select(".upload-step.step1");
    const stepInput = this.select(".upload-step.step2");
    stepInput.classList.remove("hidden");
    stepInput.classList.add("no-margin");
    await this.animateEl(upload, "leave-to");
    upload.classList.add("hidden");
    upload.classList.remove("leave-to");
    
    this.isAnimating = false;
  }

  private async showStep2() {
    if (this.isAnimating) return true;
    this.isAnimating = true;

    const dropzoneText = this.select(".upload-text.upload-text-drop");
    const dropProgress = this.select(".upload-drop-progress");
    await this.animateEl(dropzoneText, "no-active");
    await this.animateEl(dropProgress, "active");
    await this.animateEl(dropProgress, "leave");

    const uploadGallery = this.select(".upload-gallery");
    uploadGallery.classList.remove("no-active");
    await this.animateEl(uploadGallery, "active");
    await this.showImages();
    const imgCount = this.select(".upload-img-count");
    await this.animateEl(imgCount, "enter");
    const stepInput = this.select(".upload-step.step2");
    stepInput.classList.remove("hidden");
    const scroll = this.select(".upload-scroll");
    return setTimeout(() => {
      scroll.scrollTo({ top: scroll.scrollHeight - scroll.clientHeight, behavior: "smooth" });
      this.isAnimating = false;
    }, 700);
  }

  private async showFromStep2ToStep1() {
    if (this.isAnimating) return true;
    this.isAnimating = true;
    const skipBtn = this.select(".btn-skip"); 
    skipBtn.classList.remove("hidden");
    const step1 = this.select(".upload-step.step1");
    step1.classList.remove("no-skip-btn");
    const imgCount = this.select(".upload-img-count");
    await this.animateEl(imgCount, "enter", "reverse-mode");
    const images = Array.from(this.el.shadowRoot.querySelectorAll(".upload-img-wrap"));
    await this.animateGroupEl(images, "leave");
    const scroll = this.select(".upload-scroll");
    scroll.scrollTo({ top: 0, behavior: "smooth" });
    const dropzoneText = this.select(".upload-text.upload-text-drop");
    const dropProgress = this.select(".upload-drop-progress");
    const stepInput = this.select(".upload-step.step2");
    dropProgress.classList.add("no-active");

    await this.animateGroupEl(images, "leave-to2");
    images.forEach(image => image.classList.remove("enter", "leave", "leave-to2"));

    dropProgress.classList.remove("active", "no-active", "leave");
    await this.animateEl(dropzoneText, "no-active", "reverse-mode");

    this.uppy.removeFile(this.uppy.getFiles()[0].id); // TODO check exception (this.uppy.getFiles() undefined)
    this.thumbnailLinks = [];
    const uploadGallery = this.select(".upload-gallery");
    stepInput.classList.add("hidden");
    await this.animateEl(uploadGallery, "active", "reverse-mode");
    uploadGallery.classList.add("no-active");
    this.isAnimating = false;
  }

  private async showStep3() {
    if (this.isAnimating) return true;
    this.isAnimating = true;
    const stepDrop = this.select(".upload-step.step1");
    const stepInput = this.select(".upload-step.step2");
    const stepProgress = this.select(".upload-step.step3");
    const scroll = this.select(".upload-scroll");

    stepProgress.classList.remove("hidden");
    scroll.scrollTo({ top: scroll.scrollHeight - scroll.clientHeight, behavior: "smooth" });
    await this.animateEl(stepProgress, "enter");
    stepDrop.classList.add("hidden");
    stepInput.classList.add("hidden");
    this.isTextReview ? this.uploadTextReview() : this.uppy.upload();
    this.isAnimating = false;
  }

  private async showStep4() {
    if (this.isAnimating) return true;

    const stepProgress = this.select(".upload-step.step3");
    const stepComplete = this.select(".upload-step.step4");
    const scroll = this.select(".upload-scroll");
    const uploadGallery = this.select(".upload-gallery");
    const skipBtn = this.select(".btn-skip");
    skipBtn.classList.remove("hidden");

    stepComplete.classList.remove("hidden");
    this.initDefaultSettings();
    await this.animationDelay(500);
    scroll.scrollTo({ top: scroll.scrollHeight - scroll.clientHeight, behavior: "smooth" });
    await this.animateEl(stepComplete, "enter");
    stepProgress.classList.remove("enter");
    stepProgress.classList.add("hidden");
    uploadGallery.classList.remove("active");
    uploadGallery.classList.add("no-active");
    this.isAnimating = false;
  }

  private async showFromStep4ToStep1() {
    if (this.isAnimating) return true;

    this.isAnimating = true;
    const stepDrop = this.select(".upload-step.step1");
    const stepComplete = this.select(".upload-step.step4");
    const dropzoneText = this.select(".upload-text.upload-text-drop");
    const SVGDropFiles = this.select(".svg-drop-files");

    stepDrop.classList.add("leave");
    SVGDropFiles.classList.add("leave");
    await this.animateEl(stepComplete, "leave");
    stepDrop.classList.remove("hidden");
    stepDrop.classList.add("leave");
    this.clearAllData();
    await this.animationDelay(100);
    await this.animateEl(stepDrop, "enter");
    await this.animateEl(SVGDropFiles, "enter");
    await this.animateEl(dropzoneText, "enter");
    stepDrop.classList.remove("leave", "enter");
    stepComplete.classList.remove("leave", "enter");
    stepComplete.classList.add("hidden");
    this.isAnimating = false;
  }

  private animateGroupEl(arr: Element[], animatedClass: string) {
    const animationGroup = [];
    arr.forEach(el => animationGroup.push(this.animateEl(el, animatedClass)));
    return Promise.all(animationGroup);
  }

  private animateEl(el: Element, animatedClass: string, reverse: string = "no") {
    return new Promise(resolve => {
      if (el.classList.contains(animatedClass)) resolve(true);

      const animationHander = () => resolve(el.removeEventListener("transitionend", animationHander));
      el.addEventListener("transitionend", animationHander);
      reverse === "reverse-mode"
        ? el.classList.remove(animatedClass)
        : el.classList.add(animatedClass);
    });
  }

  private animationDelay(time: number) {
    return new Promise(resolve => setTimeout(() => { resolve(true); }, time));
  }

  private getScrollbarWidth() {
    // Creating invisible container
    const outer = document.createElement("div");
    outer.style.visibility = "hidden";
    outer.style.overflowY = "scroll"; // forcing scrollbar to appear
    outer.style.display = "block"; // disable possible default reset settings
    document.body.appendChild(outer);
    // Creating inner element and placing it in the container
    const inner = document.createElement("div");
    inner.style.width = "100%"; // disable possible default reset settings
    inner.style.display = "block"; // disable possible default reset settings
    outer.appendChild(inner);
    // Calculating difference between container's full width and the child width
    const scrollbarWidth = (outer.offsetWidth - inner.offsetWidth);
    // Removing temporary elements from the DOM
    outer.parentNode.removeChild(outer);

    return scrollbarWidth === 0
      ? { width: 17, paddingRgiht: 17 } // set padding right compensation for mac devices
      : { width: scrollbarWidth, paddingRgiht: 0 };
  }

  private onUploadClickHandler = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const dropzone: HTMLElement = this.el.shadowRoot.querySelector(".dropzone-graphics");
    return dropzone.click();
  }

  private onRemoveImageHandler(e: MouseEvent, link: string) {
    e.preventDefault();
    e.stopPropagation();
    this.onRemoveImage(link);
  }

  private async onRemoveImage(link: string) {
    const images = Array.from(this.selectAll(".upload-img-wrap"));
    if (this.thumbnailLinks.length === 1) return this.showFromStep2ToStep1();
    await this.animateGroupEl(images, "leave");
    images.forEach(image => image.classList.remove("enter"));
    images.forEach(image => image.classList.remove("leave"));
    this.thumbnailLinks = this.thumbnailLinks.filter(imgLink => imgLink !== link);
    const uploadedFiles = this.uppy.getFiles();
    const deleteFile = uploadedFiles.find(file => file.preview === link);
    this.uppy.removeFile(deleteFile.id);
    await this.animationDelay(20);
    const updatedImages = Array.from(this.selectAll(".upload-img-wrap"));
    updatedImages.forEach(image => image.classList.remove("before-enter"));
    return await this.animateGroupEl(updatedImages, "enter");
  }

  private clearAllData() {
    const uploadedFiles = this.uppy.getFiles();
    uploadedFiles.forEach(file => this.uppy.removeFile(file.id));
    this.thumbnailLinks = [];
    this.postData = {
      caption: "",
      source_user_id: "",
      source_user_email: "",
      rating: null
    };
    return this.postData;
  }

  render() {
    const starsArray = [1, 2, 3, 4, 5];

    return (
      <Host style={this.setPopupCSSVariables()} class="needsclick" /* FIXME: https://github.com/ftlabs/fastclick/issues/604 */>
        <div class={{ "popup": true, "animated": this.appState.settings.popup_animation, "upload_widget": true }} tabindex={this.uploadTabIndex} role="dialog" aria-label="Upload">
          <div class="upload">
            {!this.standalone &&
              <button class="upload-close" type="button" data-dismiss="popup" onClick={this.hidePopupClickHandler} aria-label="popup close"></button>
            }
            <form class="upload-form">
              <div class="upload-scroll">
                {/* step 1 */}
                <div class="upload-step step1">
                  <h2 class="upload-h2">{this.appState.settings.customer_photo_upload_title}</h2>
                  <h3 class="upload-h3">{this.appState.settings.customer_photo_upload_body}</h3>
                  <div class="dropzone">
                    <div class="dropzone-graphics" onDragOver={this.onDragOverHandler} onDrop={this.onDropHandler} onClick={this.openDialog}>
                      {SVGDropdown()}
                      <div class="upload-drop">
                        <div class="upload-text upload-text-drop">{this.appState.settings.customer_photo_upload_drop_files_label}</div>
                        <span class="upload-drop-progress"></span>
                      </div>
                      <input type="file" hidden name="files[]" multiple accept="image/png,image/gif,image/jpg,image/jpeg" onChange={this.onChangeHandler} aria-label={this.appState.settings.customer_photo_upload_drop_files_label} />
                    </div>
                    <div class="upload-gallery no-active" onDragOver={this.onDragOverHandler} onDrop={this.onDropHandler}>
                      {this.thumbnailLinks.map((link, index) =>
                        <div class={`upload-img-wrap wrap-pos-${index + 1} img-count-${this.thumbnailLinks.length} before-enter`}
                          onClick={(e) => this.onRemoveImageHandler(e, link)}>
                          <img src={link} alt={`uploaded img preview ${index + 1}/${this.thumbnailLinks.length}`} class="upload-img" />
                          <span class="icon-cross"></span>
                        </div>
                      )}
                      {this.thumbnailLinks.length < 4 &&
                        <span class={`upload-img-wrap wrap-pos-${this.thumbnailLinks.length + 1} upload-img-upload img-count-${this.thumbnailLinks.length} before-enter`}
                          onClick={this.onUploadClickHandler}>
                          <span class="icon-cross plus"></span>
                        </span>
                      }
                    </div>
                    <span class="upload-img-count">({this.thumbnailLinks.length}/4)</span>
                  </div>
                  <button type="button" class="btn-skip" onClick={() => this.showSkipToStep2()} aria-label="skip upload image for text review">
                    Skip
                    <IconDown></IconDown>
                  </button>
                </div>

                {/* step 2 */}
                <div class="upload-step step2 hidden">
                  <div class="star-rating"> 
                    {starsArray.map((el, index) =>
                      <span class={{ "icon-star": true, "active": el <= this.starRating, "warning": this.showFormWarning && this.postData["rating"] === null }}
                        onClick={(e: MouseEvent) => this.starClickHandler(e, index)}
                        onMouseEnter={() => { this.starRating = index + 1; this.showFormWarning = false; }}
                        onMouseLeave={() => this.starRating = this.postData["rating"]}
                      >
                        {SVGStar()}
                      </span>)}
                  </div>

                  <input type="text" class={{ "form-control": true, "warning": this.showFormWarning && !this.isInputValid("source_user_id") }} placeholder={this.appState.settings.customer_photo_upload_form_name_placeholder} 
                    name="source_user_id" onInput={this.postDataChangeHandler} value={this.postData["source_user_id"]} 
                    required aria-label={this.appState.settings.customer_photo_upload_form_name_placeholder} />

                  <input type="email" class={{ "form-control": true, "warning": this.showFormWarning && !this.isInputValid("source_user_email") }} 
                    placeholder={this.appState.settings.customer_photo_upload_form_email_placeholder} name="source_user_email" onInput={this.postDataChangeHandler}
                    value={this.postData["source_user_email"]} required aria-label={this.appState.settings.customer_photo_upload_form_email_placeholder} />

                  <textarea class={{ "form-control form-textarea": true, "warning": this.showFormWarning && !this.isInputValid("caption") }} rows={6} placeholder={this.appState.settings.customer_photo_upload_form_body_placeholder} name="caption" onInput={this.postDataChangeHandler}
                    value={this.postData["caption"]} required aria-label={this.appState.settings.customer_photo_upload_form_body_placeholder}></textarea>

                  {this.appState.settings.customer_photo_upload_form_legal_message !== "" &&
                    <p class="upload-text privacy" innerHTML={this.appState.settings.customer_photo_upload_form_legal_message}></p>
                  }
                  <button type="submit" class={{ "upload-submit": true }}
                    onClick={this.handleSendClick} aria-label={this.appState.settings.customer_photo_upload_form_send_label}>{this.appState.settings.customer_photo_upload_form_send_label}</button>
                </div>

                {/* step 3 */}
                <div class="upload-step step3 hidden">
                  <h2 class="upload-h2 progress">{this.appState.settings.customer_photo_upload_form_uploading}</h2>
                  <div class="progress-bar"></div>
                </div>

                {/* step 4 */}
                <div class="upload-step step4 hidden">
                  {SVGCompleted()}
                  <p class="upload-text completed" innerHTML={this.appState.settings.customer_photo_upload_complete_body}></p>
                  <button class="upload-submit close" onClick={this.handleCloseClick} aria-label="form submit" type="button">{this.appState.settings.customer_photo_upload_complete_close_label}</button>
                </div>
              </div>
            </form>
            {!this.appState.settings.hide_branding &&
              <a class="upload-brand-link" href={`https://apps.shopify.com/covet-pics?utm_source=embed&utm_medium=upload&utm_campaign=${location.host}`} target="_blank" rel="noopener" aria-label="Covet.pics logo">
                {SVGBrand()}
              </a>
            }
          </div>
          <div class="backdrop" onClick={this.hidePopupClickHandler}></div>
          <div class={{ "notification": true, "active": this.isNotificationActive }}>
            <p class="notification-message">error: {this.notificationMessage}</p>
          </div>
        </div>
      </Host>
    );
  }
}
